import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { LinkedLogo, NamedLink } from '../../../../components';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { useHistory } from 'react-router-dom';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { apiBaseUrl } from '../../../../util/api';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : false;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';
  const isArabicDefault = intl.locale.toLowerCase().startsWith('ar');
  // use block builder instead of mapping blocks manually
  const [isArabic, setIsArabic] = useState(isArabicDefault);
  // Initialize all sections as closed
  const [openSections, setOpenSections] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  }); // keyed by index
  const history = useHistory();
  const isRtl = (intl.locale || '').toLowerCase().startsWith('ar');

  // Initialize language state from URL (pathname/query) and intl locale
  useEffect(() => {
    try {
      const loc = history && history.location ? history.location : null;
      const pathname =
        loc?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const search = loc?.search || (typeof window !== 'undefined' ? window.location.search : '');
      const sp = new URLSearchParams(search || '');
      const langQp = sp.get('lang');
      const isAr =
        langQp === 'ar' ||
        pathname === '/p/ar-home' ||
        (intl?.locale || '').toLowerCase().startsWith('ar');
      setIsArabic(!!isAr);
    } catch (_) {
      // no-op
    }
  }, [history, intl]);

  // React to global languageChange events as fired by Topbar
  useEffect(() => {
    const onLanguageChange = e => {
      const next = e?.detail?.locale === 'ar' ? 'ar' : 'en';
      setIsArabic(next === 'ar');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('languageChange', onLanguageChange);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('languageChange', onLanguageChange);
      }
    };
  }, []);

  const handleFooterLanguageChange = language => {
    try {
      localStorage.setItem('selectedLanguage', language);
    } catch (_) {}
    setIsArabic(language === 'ar');
    // Update document direction for immediate UX feedback
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', language === 'ar' ? 'ar' : 'en');
    // Keep user on the same page by default; update only `lang` query param.
    // Landing page special-cases:
    // - EN: navigate to '/'
    // - AR: navigate to '/p/ar-home'
    try {
      const loc = history && history.location ? history.location : null;
      const pathname =
        loc?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const search = loc?.search || (typeof window !== 'undefined' ? window.location.search : '');
      const hash = loc?.hash || (typeof window !== 'undefined' ? window.location.hash : '');
      const sp = new URLSearchParams(search || '');
      const isEnglishLanding = pathname === '/';
      const isArabicLanding = pathname === '/p/ar-home';

      let nextPath = pathname;
      if (language === 'en') {
        // English: go to normal landing if currently on Arabic landing
        if (isArabicLanding) nextPath = '/';
        // Prefer clean URLs by removing lang param
        sp.delete('lang');
      } else {
        // Arabic: go to Arabic landing if currently on normal landing
        if (isEnglishLanding) nextPath = '/p/ar-home';
        else sp.set('lang', 'ar');
      }

      const next = { pathname: nextPath, search: sp.toString(), hash, state: loc?.state };
      history && history.push(next);
    } catch (_) {
      try {
        const url = new URL(window.location.href);
        const isEnglishLanding = url.pathname === '/';
        const isArabicLanding = url.pathname === '/p/ar-home';

        if (language === 'en') {
          url.searchParams.delete('lang');
          if (isArabicLanding) url.pathname = '/';
        } else {
          if (isEnglishLanding) url.pathname = '/p/ar-home';
          else url.searchParams.set('lang', 'ar');
        }
        window.history.replaceState(window.history.state || {}, '', url.toString());
      } catch (__) {}
    }
    // Notify app (DynamicIntlProvider) and Topbar to update
    try {
      window.dispatchEvent(
        new CustomEvent('languageChange', { detail: { locale: language === 'ar' ? 'ar' : 'en' } })
      );
    } catch (_) {}
  };

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 660 : false
  );

  // Handle window resize
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 660);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSection = idx => {
    if (!isMobile) return; // only accordion on mobile <= 660px
    setOpenSections(prev => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const draftId = '00000000-0000-0000-0000-000000000000';

  // Newsletter subscription state (inline, no modal)
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async e => {
    e.preventDefault();

    // Validate email
    if (!newsletterEmail.trim()) {
      setSubmitStatus('error');
      setSubmitMessage(
        intl.formatMessage({
          id: 'Footer.emailRequired',
          defaultMessage: 'Email is required',
        })
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) {
      setSubmitStatus('error');
      setSubmitMessage(
        intl.formatMessage({
          id: 'Footer.emailInvalid',
          defaultMessage: 'Please enter a valid email address',
        })
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      // Use the correct API URL based on environment
      const response = await fetch(`${apiBaseUrl()}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newsletterEmail,
          firstName: '', // Empty for footer subscription
          lastName: '', // Empty for footer subscription
        }),
        credentials: 'include',
      });

      const data = await response.json();

      if (data.success) {
        // Check if it's a duplicate subscription
        if (data.duplicate) {
          setSubmitStatus('info');
          setSubmitMessage(
            data.message ||
              intl.formatMessage({
                id: 'Footer.alreadySubscribed',
                defaultMessage: 'You are already subscribed to our newsletter',
              })
          );
        } else {
          setSubmitStatus('success');
          setSubmitMessage(
            data.message ||
              intl.formatMessage({
                id: 'Footer.subscribeSuccess',
                defaultMessage: 'Thank you for subscribing!',
              })
          );
        }
        setNewsletterEmail(''); // Clear the input
      } else {
        setSubmitStatus('error');
        setSubmitMessage(
          data.message ||
            intl.formatMessage({
              id: 'Footer.subscribeError',
              defaultMessage: 'Something went wrong. Please try again.',
            })
        );
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubmitStatus('error');
      setSubmitMessage(
        intl.formatMessage({
          id: 'Footer.networkError',
          defaultMessage: 'Network error. Please try again later.',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footerWrapper}>
        <div className={css.innerWrapper}>
          <div className={css.contentBox}>
            <div className={css.footerLeftSide}>
              <div>
                <div className={css.mainLogo}>
                  <IconCard type="whitelogo" />
                </div>
                <div className={css.subscribeBox}>
                  <form onSubmit={handleNewsletterSubmit} className={css.subscribeForm}>
                    <input
                      type="email"
                      value={newsletterEmail}
                      onChange={e => setNewsletterEmail(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'Footer.subscribePlaceholder' })}
                      disabled={isSubmitting}
                      className={submitStatus === 'error' ? css.inputError : ''}
                    />
                    <button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <FormattedMessage id="Footer.subscribing" defaultMessage="..." />
                      ) : (
                        <FormattedMessage id="Footer.subscribeButton" />
                      )}
                    </button>
                  </form>
                  {submitStatus && (
                    <div
                      className={
                        submitStatus === 'success'
                          ? css.successMessage
                          : submitStatus === 'info'
                          ? css.infoMessage
                          : css.errorMessage
                      }
                    >
                      {submitMessage}
                    </div>
                  )}
                </div>
                <div className={css.stayText}>
                  <FormattedMessage id="Footer.subscribeText" />
                </div>
              </div>
              <div className={css.subscribeText}>
                <FormattedMessage id="Footer.tagline" />
              </div>
            </div>
            <div className={css.rightSide}>
              <div className={css.mainLinks}>
                <div className={classNames(css.linksCol, { [css.open]: openSections[0] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(0)}>
                    <span>
                      <FormattedMessage id="Footer.platform" />
                    </span>
                    <span className={css.accordionIcon}>
                      {openSections[0] ? <IconCard type="minus" /> : <IconCard type="plus" />}
                    </span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name="LandingPage">
                        <FormattedMessage id="Footer.platform.home" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: 'how-it-works' }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.platform.howItWorks" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="SearchPage">
                        <FormattedMessage id="Footer.platform.findTutor" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-live-classes' : 'live-classes' }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.platform.liveClasses" />
                      </NamedLink>
                    </li>
                    <li>
                      {/* <NamedLink 
                        name= 'EditListingPage'
                        params={{ slug: 'draft', id: draftId, type: 'new', tab: 'details' }}
                      >
                        <FormattedMessage id="Footer.platform.becomeTutor" />
                      </NamedLink> */}
                      <a href="/p/become-a-tutor">
                        <FormattedMessage id="Footer.platform.becomeTutor" />
                      </a>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-tutor-onboarding' : 'tutor-onboarding' }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.platform.tutorOnboarding" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[1] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(1)}>
                    <span>
                      <FormattedMessage id="Footer.learning" />
                    </span>
                    <span className={css.accordionIcon}>
                      {openSections[1] ? <IconCard type="minus" /> : <IconCard type="plus" />}
                    </span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{
                          pageId: isArabic ? 'ar-curriculum-overview' : 'curriculum-overview',
                        }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.learning.curriculum" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="SearchPage"
                        to={{ search: '?pub_ageGroup=has_any:kids,teenagers,adults' }}
                      >
                        <FormattedMessage
                          id="Footer.learning.levels"
                          defaultMessage="Levels & Age Groups"
                        />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-msa-dialects' : 'msa-dialects' }}
                      >
                        <FormattedMessage id="Footer.learning.msaDialects" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink name="FaqPage" className={css.topbarLink}>
                        <FormattedMessage id="Footer.learning.faq" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{
                          pageId: isArabic ? 'ar-learning-resources' : 'learning-resources',
                        }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.learning.learningResources" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[3] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(3)}>
                    <span>
                      <FormattedMessage id="Footer.forParents" />
                    </span>
                    <span className={css.accordionIcon}>
                      {openSections[3] ? <IconCard type="minus" /> : <IconCard type="plus" />}
                    </span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name="FamilyDashboardPage">
                        <FormattedMessage
                          id="Footer.forParents.dashboard"
                          defaultMessage="Parent Dashboard"
                        />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-track-your-path' : 'track-your-path' }}
                      >
                        <FormattedMessage
                          id="Footer.forParents.progress"
                          defaultMessage="Progress Reports"
                        />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-safety-privacy' : 'safety-privacy' }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.forParents.safety" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-track-your-path' : 'track-your-path' }}
                      >
                        <FormattedMessage id="Footer.forParents.trackYourPath" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[2] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(2)}>
                    <span>
                      <FormattedMessage id="Footer.company" />
                    </span>
                    <span className={css.accordionIcon}>
                      {openSections[2] ? <IconCard type="minus" /> : <IconCard type="plus" />}
                    </span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-about' : 'about' }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.company.about" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-our-mission' : 'our-mission' }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.company.mission" />
                      </NamedLink>
                    </li>

                    <li>
                      <NamedLink name="BlogMainPage" className={css.topbarLink}>
                        <FormattedMessage id="Footer.company.blog" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-press-media' : 'press-media' }}
                        className={css.topbarLink}
                      >
                        <FormattedMessage id="Footer.company.pressMedia" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[4] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(4)}>
                    <span>
                      <FormattedMessage id="Footer.support" />
                    </span>
                    <span className={css.accordionIcon}>
                      {openSections[4] ? <IconCard type="minus" /> : <IconCard type="plus" />}
                    </span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name="FaqPage">
                        <FormattedMessage
                          id="Footer.support.helpCenter"
                          defaultMessage="Help Center"
                        />
                      </NamedLink>
                    </li>
                    {/* Note: Live Chat link removed until real-time chat infrastructure and CMS support page are officially available */}
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-contact-us' : 'contact-us' }}
                      >
                        <FormattedMessage id="Footer.support.contact" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{
                          pageId: isArabic ? 'ar-term-and-condition' : 'term-and-condition',
                        }}
                      >
                        <FormattedMessage id="Footer.support.terms" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-privacy-policy' : 'privacy-policy' }}
                      >
                        <FormattedMessage id="Footer.support.privacy" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{
                          pageId: isArabic ? 'ar-cancellation-policy' : 'cancellation-policy',
                        }}
                      >
                        <FormattedMessage id="Footer.support.cancellationPolicy" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{
                          pageId: isArabic ? 'ar-refund-policy' : 'refund-policy',
                        }}
                      >
                        <FormattedMessage id="Footer.support.refundPolicy" />
                      </NamedLink>
                    </li>
                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-contact-us' : 'contact-us' }}
                      >
                        <FormattedMessage
                          id="Footer.support.technicalSupport"
                          defaultMessage="Technical Support"
                        />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
                <div className={classNames(css.linksCol, { [css.open]: openSections[5] })}>
                  <div className={css.linksHeading} onClick={() => toggleSection(5)}>
                    <span>
                      <FormattedMessage id="Footer.payments" />
                    </span>
                    <span className={css.accordionIcon}>
                      {openSections[5] ? <IconCard type="minus" /> : <IconCard type="plus" />}
                    </span>
                  </div>
                  <ul>
                    <li>
                      <NamedLink name="SubscriptionPlan">
                        <FormattedMessage id="Footer.payments.pricing" />
                      </NamedLink>
                    </li>
                    {/* Note: Referral Program and Gift Cards links removed until backend modules and CMS pages are officially ready */}
                    <li>
                      <NamedLink
                        name="FaqPage"
                        to={{ search: '?tab=payment-wallet', hash: '#payment-wallet' }}
                      >
                        <FormattedMessage
                          id="Footer.payments.walletInfo"
                          defaultMessage="Wallet Info"
                        />
                      </NamedLink>
                    </li>

                    <li>
                      <NamedLink
                        name="CMSPage"
                        params={{ pageId: isArabic ? 'ar-secure-payments' : 'secure-payments' }}
                      >
                        <FormattedMessage id="Footer.payments.securePayments" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>
              </div>
              <div className={css.footerBottom}>
                <div className={css.mobileText}>
                  <FormattedMessage id="Footer.tagline" />
                </div>
                <div className={css.languageButton}>
                  <div className={css.toggleWrapper}>
                    <span className={`${css.label} ${isArabic ? css.active : ''}`}>AR</span>
                    <div
                      className={`${css.toggle} ${
                        isRtl ? (isArabic ? css.right : css.left) : isArabic ? css.left : css.right
                      }`}
                      onClick={() => handleFooterLanguageChange(isArabic ? 'en' : 'ar')}
                    >
                      <div className={css.circle}></div>
                    </div>
                    <span className={`${css.label} ${!isArabic ? css.active : ''}`}>EN</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={css.footerBottomBox}>
            <div className={css.copyRightText}>
              <FormattedMessage
                id="Footer.copyright"
                values={{ year: new Date().getFullYear() }}
                defaultMessage={`© ${new Date().getFullYear()} Arabibi. All rights reserved.`}
              />
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
